/**
 * The one writer of the brand custom properties on the document root.
 *
 * Two callers want to drive the palette — the shell, with what is saved, and
 * the settings tab, with what is being edited. When each owned its own writes,
 * whichever unmounted last stripped the properties the other had set and the
 * app fell back to the default palette. Here the layers are held in one place
 * and the root is repainted from whichever is on top.
 */

import { generateScale, SCALE_STEPS } from './colorScale';
import { generateNeutralScale, NEUTRAL_STEPS } from './neutralScale';

const STORAGE_KEY = 'edify_brand_colors';

export interface BrandColors {
  primary?: string;
  secondary?: string;
}

let base: BrandColors = {};
let preview: BrandColors | null = null;
let themeWatched = false;

function writeScale(
  prefix: string,
  steps: readonly number[],
  scale: Record<number, string> | null,
): void {
  const { style } = document.documentElement;
  for (const step of steps) {
    const property = `--color-${prefix}-${step}`;
    if (scale) style.setProperty(property, scale[step]);
    else style.removeProperty(property);
  }
}

function paint(): void {
  const { primary, secondary } = preview ?? base;
  const dark = document.documentElement.classList.contains('dark');

  writeScale('brand', SCALE_STEPS, primary ? generateScale(primary) : null);
  writeScale('accent', SCALE_STEPS, secondary ? generateScale(secondary) : null);
  writeScale('neutral', NEUTRAL_STEPS, primary ? generateNeutralScale(primary, dark) : null);
}

/**
 * Repaints when the theme flips.
 *
 * An inline custom property outranks the `.dark` rule in `tokens.css`, so the
 * dark neutral ramp can only come from here once the light one has been set.
 */
function watchTheme(): void {
  if (themeWatched) return;
  themeWatched = true;
  new MutationObserver(paint).observe(document.documentElement, { attributeFilter: ['class'] });
}

function persist(colors: BrandColors): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(colors));
  } catch {
    // A full or blocked store only costs the head start on the next load.
  }
}

/** Applies the saved colours. Ignores an empty update so a pending fetch paints nothing. */
export function setBaseBrandColors(colors: BrandColors): void {
  if (!colors.primary && !colors.secondary) return;
  base = colors;
  persist(colors);
  watchTheme();
  paint();
}

/** Applies colours being edited on top of the saved ones. Null drops back to them. */
export function setPreviewBrandColors(colors: BrandColors | null): void {
  preview = colors;
  watchTheme();
  paint();
}

/**
 * Paints the colours from the last session before the first render.
 *
 * The organization is fetched, so without this every reload shows the default
 * palette until the response lands.
 */
export function restoreBrandColors(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const stored = JSON.parse(raw) as Partial<BrandColors>;
    setBaseBrandColors({
      primary: typeof stored.primary === 'string' ? stored.primary : undefined,
      secondary: typeof stored.secondary === 'string' ? stored.secondary : undefined,
    });
  } catch {
    localStorage.removeItem(STORAGE_KEY);
  }
}

/** Drops the palette so the next organization is not previewed in this one's colours. */
export function clearBrandColors(): void {
  base = {};
  preview = null;
  localStorage.removeItem(STORAGE_KEY);
  paint();
}
